import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import PrintIcon from "@material-ui/icons/Print";
import { makeStyles } from "@material-ui/core/styles";
import { authGet, authPut } from "../../lib";
import AdminNotesCard from "./Components/AdminNotesCard";
import ServicePanelDialog from "./Components/ServicePanelDialog";

/**
 * One repair request — a port of rma_details.php / rma_details.tpl.
 *
 * The legacy "Generate PDF" button shelled out to wkhtmltopdf, fetching the
 * page back over HTTP with a hardcoded password. This prints instead: same
 * output, no binary to install, no credentials in a command line, and it uses
 * the print stylesheet the order screens already have.
 */

const STATUS_COLOR = {
  Pending: "#0d47a1",
  Approved: "#1b5e20",
  Denied: "#b71c1c",
  Cancelled: "#8d6e4f",
};

const useStyles = makeStyles((theme) => ({
  page: { maxWidth: 1360, margin: "24px auto 0", width: "100%" },
  head: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
  },
  title: { fontSize: 17, fontWeight: 500 },
  spacer: { flex: 1 },
  stamp: { color: theme.palette.text.secondary, fontSize: 13 },
  badge: {
    padding: "2px 8px",
    borderRadius: 3,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: ".04em",
    color: "#fff",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  panel: { padding: theme.spacing(2) },
  wide: { padding: theme.spacing(2), marginBottom: theme.spacing(2) },
  panelTitle: {
    display: "flex",
    alignItems: "center",
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: ".02em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.25),
  },
  editBtn: { padding: 2, marginLeft: 4, color: "rgba(0,0,0,0.4)", "&:hover": { color: "#315179" } },
  line: { fontSize: 13, lineHeight: 1.85 },
  label: { color: theme.palette.text.secondary },
  muted: { color: theme.palette.text.secondary },
  rmaField: { width: 140, marginRight: theme.spacing(2) },
  actions: {
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
    flexWrap: "wrap",
  },
  description: { fontSize: 13, lineHeight: 1.7, whiteSpace: "pre-wrap" },
  notice: {
    padding: theme.spacing(1, 1.5),
    marginBottom: theme.spacing(1.5),
    borderRadius: 3,
    fontSize: 13,
    background: "rgba(27,94,32,0.10)",
    color: "#1b5e20",
  },
  error: { color: "#c62828", fontSize: 13, marginTop: theme.spacing(1) },
  loading: { padding: theme.spacing(6), textAlign: "center" },
}));

const when = (s) => {
  if (!s) return "";
  const d = new Date(String(s).replace(" ", "T"));
  return Number.isNaN(d.getTime()) ? String(s) : d.toLocaleString();
};

const onlyDate = (s) => (s ? String(s).slice(0, 10) : "");

export function RepairDetailPage() {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams();

  const [data, setData] = useState(null);
  const [notes, setNotes] = useState([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const [rmaNo, setRmaNo] = useState("");
  const [comments, setComments] = useState("");
  const [denied, setDenied] = useState(false);
  const [cancelled, setCancelled] = useState(false);

  const load = useCallback(async () => {
    setError("");
    const { data: res, error: err } = await authGet(`/repairs/${id}`);
    if (err) {
      setError(err.message || "Could not load that repair.");
      return;
    }
    setData(res);
    setNotes(res.notes ?? []);
    setRmaNo(res.repair?.rma_no && !["DENIED", "CANCELLED"].includes(res.repair.rma_no)
      ? res.repair.rma_no
      : "");
    setComments(res.repair?.comments ?? "");
    setDenied(res.repair?.rma_no === "DENIED");
    setCancelled(res.repair?.rma_no === "CANCELLED");
  }, [id]);

  useEffect(() => {
    setData(null);
    load();
  }, [load]);

  const save = async () => {
    setBusy(true);
    setError("");
    setNotice("");
    const { data: res, error: err } = await authPut(`/repairs/${id}/status`, {
      rma_no: rmaNo,
      comments,
      denied,
      cancelled,
    });
    setBusy(false);

    if (err) {
      setError(err.message || "Could not update.");
      return;
    }
    setNotice(`Updated — ${res.status}.`);
    load();
  };

  if (error && !data) {
    return (
      <Box className={classes.page}>
        <div className={classes.error}>{error}</div>
      </Box>
    );
  }
  if (!data) {
    return (
      <Box className={classes.page}>
        <div className={classes.loading}>
          <CircularProgress size={24} />
        </div>
      </Box>
    );
  }

  const r = data.repair;
  const go = (target) => () => history.push(`/repairs/${target}`);

  return (
    <Box className={`${classes.page} order-print`}>
      <div className={classes.head}>
        <Button size="small" variant="outlined" className="no-print"
          onClick={() => history.push("/repairs")}>
          ← Repairs
        </Button>
        <Button size="small" variant="outlined" className="no-print"
          disabled={!data.prevId} onClick={go(data.prevId)}>
          ‹ Prev
        </Button>
        <Button size="small" variant="outlined" className="no-print"
          disabled={!data.nextId} onClick={go(data.nextId)}>
          Next ›
        </Button>

        <Typography className={classes.title}>Repair #: {r.rma_id}</Typography>
        <span className={classes.badge} style={{ background: STATUS_COLOR[r.status] }}>
          {r.status.toUpperCase()}
        </span>
        {!!Number(r.preowned) && (
          <span className={classes.badge} style={{ background: "#4a148c" }}>PRE-OWNED</span>
        )}

        <div className={classes.spacer} />
        <span className={classes.stamp}>{when(r.created)}</span>
        <IconButton size="small" className={`${classes.editBtn} no-print`}
          title="Print this repair" onClick={() => window.print()}>
          <PrintIcon fontSize="small" />
        </IconButton>
      </div>

      {notice && <div className={classes.notice}>{notice}</div>}

      <div className={classes.grid}>
        <Paper className={classes.panel}>
          <div className={classes.panelTitle}>RMA Information</div>
          <div className={classes.line}>
            <span className={classes.label}>Pre-owned:</span>{" "}
            {Number(r.preowned) ? "Yes" : "No"}
          </div>

          <div style={{ marginTop: 10 }}>
            <TextField
              className={classes.rmaField}
              label="RMA Number"
              value={rmaNo}
              onChange={(e) => setRmaNo(e.target.value)}
              disabled={denied || cancelled}
            />
          </div>

          {/* Both write into rma_no, which is why they are exclusive. */}
          <FormControlLabel
            className="no-print"
            control={
              <Checkbox color="primary" checked={denied}
                onChange={(e) => { setDenied(e.target.checked); if (e.target.checked) setCancelled(false); }} />
            }
            label="Denied"
          />
          <FormControlLabel
            className="no-print"
            control={
              <Checkbox color="primary" checked={cancelled}
                onChange={(e) => { setCancelled(e.target.checked); if (e.target.checked) setDenied(false); }} />
            }
            label="Cancelled"
          />

          <TextField
            fullWidth
            multiline
            rows={3}
            label="Comments"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            style={{ marginTop: 8 }}
          />

          <div className={`${classes.actions} no-print`}>
            <Button size="small" variant="contained" color="primary" disabled={busy} onClick={save}>
              {busy ? "Saving…" : "Update"}
            </Button>
          </div>
          {error && <div className={classes.error}>{error}</div>}
        </Paper>

        <Paper className={classes.panel}>
          <div className={classes.panelTitle}>
            Contact
            <IconButton size="small" className={`${classes.editBtn} no-print`}
              title="Edit contact information" onClick={() => setEditing("contact")}>
              <EditIcon fontSize="small" />
            </IconButton>
          </div>
          <div className={classes.line}>
            {[r.first_name, r.mi, r.last_name].filter(Boolean).join(" ")}
          </div>
          <div className={classes.line}>
            <a href={`mailto:${r.email}`}>{r.email}</a>
          </div>
          <div className={classes.line}>{r.phone}</div>

          <div className={classes.panelTitle} style={{ marginTop: 16 }}>
            Shipping
            <IconButton size="small" className={`${classes.editBtn} no-print`}
              title="Edit shipping information" onClick={() => setEditing("shipping")}>
              <EditIcon fontSize="small" />
            </IconButton>
          </div>
          <div className={classes.line}>
            {[r.ship_first_name, r.ship_mi, r.ship_last_name].filter(Boolean).join(" ")}
          </div>
          <div className={classes.line}>{r.ship_address1}</div>
          {r.ship_address2 && <div className={classes.line}>{r.ship_address2}</div>}
          <div className={classes.line}>
            {r.ship_city}, {r.ship_state} {r.ship_zip}
          </div>
        </Paper>

        <Paper className={classes.panel}>
          <div className={classes.panelTitle}>
            Watch
            <IconButton size="small" className={`${classes.editBtn} no-print`}
              title="Edit watch information" onClick={() => setEditing("watch")}>
              <EditIcon fontSize="small" />
            </IconButton>
          </div>
          <div className={classes.line}>{String(r.brand || "").trim()}</div>
          <div className={classes.line}>{r.model}</div>
          <div className={classes.line}>
            <span className={classes.label}>Serial No.:</span>{" "}
            {r.serial || <span className={classes.muted}>—</span>}
          </div>
          <div className={classes.line}>
            <span className={classes.label}>Invoice #:</span>{" "}
            {r.invoice || <span className={classes.muted}>—</span>}
          </div>
          <div className={classes.line}>
            <span className={classes.label}>Purchase Date:</span>{" "}
            {onlyDate(r.purchase_date) || <span className={classes.muted}>—</span>}
          </div>
        </Paper>
      </div>

      <Paper className={classes.wide}>
        <div className={classes.panelTitle}>Problem Description</div>
        <div className={classes.description}>{r.description}</div>
      </Paper>

      <AdminNotesCard
        endpoint={`/repairs/${id}`}
        notes={notes}
        onChange={setNotes}
      />

      {editing && (
        <ServicePanelDialog
          panel={editing}
          endpoint={`/repairs/${id}`}
          values={r}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </Box>
  );
}

export default RepairDetailPage;
